import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import { Download, Share } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useAppSelector } from "../../store/hooks/useAppSelector";
import { RootState } from "../../store/types/rootState";
import { generateTalonarioImage } from "../../utils/talonarioImage";
import { getPrimaryColorByAccountId } from "../../utils/styleConstants";
import { API_URL } from "../../constants/api";

interface TalonarioImageDialogProps {
  open: boolean;
  onClose: () => void;
  limit: number;
  takenTickets: number[];
  raffleName: string;
  formatTicket: (ticket: number) => string;
}

const FILE_NAME = "talonario.png";

export const TalonarioImageDialog = ({ open, onClose, limit, takenTickets, raffleName, formatTicket }: TalonarioImageDialogProps) => {
  const account = useAppSelector(({ account }: RootState) => account);
  const [image, setImage] = useState<{ blob: Blob; url: string } | undefined>();
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!open) return;

    let objectUrl: string | undefined;
    let cancelled = false;
    setImage(undefined);
    setError(false);

    generateTalonarioImage({
      limit,
      taken: new Set(takenTickets),
      raffleName,
      accountName: account.name,
      accountUrl: account.url,
      // El bucket del logo no permite CORS: primero el logo estático del frontend y luego el de la API
      logoUrls: [`/logos/${account.id}.png`, `${API_URL}/accounts/${account.id}/logo`],
      primaryColor: getPrimaryColorByAccountId(account.id),
      formatTicket,
    })
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setImage({ blob, url: objectUrl });
      })
      .catch(() => !cancelled && setError(true));

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // formatTicket depende solo de limit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, limit, takenTickets, raffleName, account.url, account.name, account.id]);

  const file = image ? new File([image.blob], FILE_NAME, { type: "image/png" }) : undefined;
  const canShare = !!file && typeof navigator.canShare === "function" && navigator.canShare({ files: [file] });

  const handleShare = async () => {
    if (!file) return;
    try {
      await navigator.share({ files: [file], title: raffleName });
    } catch {
      // El usuario canceló la hoja de compartir
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Imagen para estados</DialogTitle>
      <DialogContent>
        {error && <Typography color="error">No se pudo generar la imagen.</Typography>}
        {!error && !image && (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        )}
        {image && (
          <img src={image.url} alt="Talonario" style={{ width: "100%", borderRadius: 8, border: "1px solid #eee" }} />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
        {image && (
          <Button component="a" href={image.url} download={FILE_NAME} startIcon={<Download />}>
            Descargar
          </Button>
        )}
        {canShare && (
          <Button variant="contained" onClick={handleShare} startIcon={<Share />}>
            Compartir
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
